import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfettiAnimation } from "@/components/ConfettiAnimation";
import { useChallengeCreationShareImage } from "@/hooks/useShareImage";
import type { ChallengeCreationShareData } from "@/api/share";

interface ChallengeSuccessState {
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  dailyGoal: number;
  totalGoal: number;
  duration: number;
  stake: number;
}

export function ChallengeSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const challengeData = location.state as ChallengeSuccessState | null;

  // Redirect if no challenge data is present
  useEffect(() => {
    if (!challengeData) {
      console.warn("No challenge data found, redirecting to My Challenges");
      navigate("/my-challenges", { replace: true });
    }
  }, [challengeData, navigate]);

  // Format date for display
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Pre-generate share image data (memoized to avoid unnecessary regeneration)
  const shareData = useMemo<ChallengeCreationShareData | null>(
    () =>
      challengeData
        ? {
            duration: challengeData.duration,
            dailyGoal: challengeData.dailyGoal,
            startDate: formatDate(challengeData.startDate),
            // Note: participantCount not available yet on creation
          }
        : null,
    [
      challengeData?.duration,
      challengeData?.dailyGoal,
      challengeData?.startDate,
    ]
  );

  // Pre-generate share image in background (parallel loading)
  const {
    share,
    isSharing,
    isReady: isShareReady,
  } = useChallengeCreationShareImage(shareData);

  const handleShare = async () => {
    if (isSharing) return;
    await share();
  };

  const handleGoToChallenges = () => {
    navigate("/my-challenges");
  };

  // Early return if no data (will redirect via useEffect)
  if (!challengeData) {
    return null;
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark p-6 overflow-hidden">
      {/* Sticky safe-area spacer */}
      <div className="sticky top-0 z-10 w-full h-[env(safe-area-inset-top)] bg-background-light dark:bg-background-dark" />
      {/* Radial Gradient Background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[150%] bg-[radial-gradient(circle_at_center,_rgba(37,244,120,0.1)_0%,_transparent_50%)] dark:bg-[radial-gradient(circle_at_center,_rgba(37,244,120,0.1)_0%,_#102217_50%,_#102217_100%)] opacity-40"></div>

      {/* Confetti Animation */}
      <ConfettiAnimation />

      {/* Main Content Container */}
      <div className="flex flex-col flex-grow z-10">
        {/* Top Section - Success Icon & Heading */}
        <div className="flex-grow flex flex-col items-center justify-center text-center pt-8">
          {/* Success Icon with Concentric Circles */}
          <div
            className="flex animate-pop-in flex-col items-center mb-5"
            style={{ animationDelay: "400ms" }}
          >
            <div className="relative flex h-32 w-32 items-center justify-center rounded-full bg-primary/10">
              {/* Glow effect */}
              <div className="absolute -inset-2 rounded-full bg-primary/20 opacity-50 blur-xl"></div>
              {/* Middle circle */}
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary/20">
                {/* Inner circle with check */}
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary">
                  <span
                    className="material-symbols-outlined text-white"
                    style={{
                      fontSize: "60px",
                      fontVariationSettings:
                        "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 48",
                    }}
                  >
                    check
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-4xl font-black tracking-tight text-content-light dark:text-white">
            Challenge Set
          </h1>
          <p className="mt-2 max-w-sm text-base text-text-secondary-light dark:text-text-secondary-dark">
            Your new challenge is live and waiting for contenders.
          </p>
        </div>

        {/* Challenge Details Card */}
        <div className="flex-shrink-0 bg-card-light/50 dark:bg-card-dark/50 backdrop-blur-sm border border-primary/20 rounded-4xl p-6 mt-8">
          {/* Card Header */}
          <div className="text-left pb-5 border-b border-primary/20">
            <h2 className="text-3xl font-bold text-content-light dark:text-white mt-1">
              {challengeData.name}
            </h2>
          </div>

          {/* 2x2 Grid Stats */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-6 pt-6">
            {/* Starts On */}
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary-light dark:text-text-secondary-dark tracking-wider">
                Starts On
              </p>
              <p className="text-lg font-semibold text-content-light dark:text-white mt-1">
                {formatDate(challengeData.startDate)}
              </p>
            </div>

            {/* Duration */}
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary-light dark:text-text-secondary-dark tracking-wider">
                Duration
              </p>
              <p className="text-lg font-semibold text-content-light dark:text-white mt-1">
                {challengeData.duration} Days
              </p>
            </div>

            {/* Daily Goal */}
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary-light dark:text-text-secondary-dark tracking-wider">
                Daily Goal
              </p>
              <p className="text-lg font-semibold text-content-light dark:text-white mt-1">
                {challengeData.dailyGoal} km/day
              </p>
            </div>

            {/* Stake */}
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary-light dark:text-text-secondary-dark tracking-wider">
                Stake
              </p>
              <p className="text-lg font-semibold text-content-light dark:text-white mt-1">
                ${challengeData.stake}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Action Buttons */}
        <div className="mt-auto pt-8 pb-4 space-y-4">
          {/* Share Challenge Button */}
          <button
            onClick={handleShare}
            disabled={!isShareReady || isSharing}
            className={`w-full font-bold py-4 px-6 rounded-full flex items-center justify-center gap-2 text-lg transition-all ${
              isShareReady && !isSharing
                ? "bg-primary text-black shadow-glow-primary active:scale-95"
                : "bg-primary/50 text-black/50 cursor-not-allowed"
            }`}
          >
            {isSharing ? (
              <>
                <span className="material-symbols-outlined font-semibold animate-spin">
                  sync
                </span>
                Sharing...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined font-semibold">
                  ios_share
                </span>
                Share Challenge
              </>
            )}
          </button>

          {/* Image ready indicator */}
          {isShareReady && !isSharing && (
            <div className="flex items-center justify-center gap-1 text-xs text-primary/60">
              <span className="material-symbols-outlined text-sm">
                check_circle
              </span>
            </div>
          )}

          {/* Go to My Challenges Button */}
          <button
            onClick={handleGoToChallenges}
            className="w-full text-center text-base font-medium py-3 text-text-secondary-light dark:text-text-secondary-dark hover:text-content-light dark:hover:text-white transition-colors"
          >
            Go to My Challenges
          </button>
        </div>
      </div>
    </div>
  );
}
